import { useCallback, useEffect, useState } from "react";
import {
    ArrowDown,
    ChevronLeft,
    ChevronRight,
    MapPin,
    Sparkles,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useJoinModal } from "@/components/landing/JoinModalProvider";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

const EVENT_IMG = "https://posthog-cyan.vercel.app/events/uoe-hackathon.png";
const MOCKUP = "https://posthog-cyan.vercel.app/mockup/build.png";

const HERO_SLIDES = [
    {
        src: EVENT_IMG,
        badge: "Hackathon · UoE",
        alt: "Build Bout hackathon at University of Eldoret",
    },
    {
        src: MOCKUP,
        badge: "Ship in the open",
        alt: "Builders collaborating on a project",
    },
    {
        src: "https://posthog-cyan.vercel.app/events/uoelast.jpg",
        badge: "Community",
        alt: "Build Bout community session",
    },
    {
        src: "https://posthog-cyan.vercel.app/events/mamuza-session.png",
        badge: "Work sessions",
        alt: "Co-working session",
    },
];

export default function Hero() {
    const { open } = useJoinModal();
    const posthog = usePostHog();

    const onRegister = () => {
        posthog?.capture("hero_register_clicked", {
            source: "hero_primary_cta",
        });
        open();
    };

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 md:pt-36 pb-16 md:pb-24"
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
                {/* Top bar: meta */}
                <div className="flex flex-wrap items-center gap-3 mb-10 md:mb-16 fade-rise">
                    <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white border border-black/10 text-xs font-medium text-zinc-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#E54D2E] pulse-dot" />
                        Live community — open for builders
                    </span>
                    <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-zinc-950 text-white text-xs font-medium">
                        <MapPin className="h-3.5 w-3.5" />
                        Nakuru, Kenya
                    </span>
                </div>

                {/* Headline + carousel */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    {/* Left: Headline */}
                    <div className="lg:col-span-8">
                        <h1
                            data-testid="hero-heading"
                            className="font-display font-black tracking-[-0.04em] text-zinc-950 leading-[0.88] text-[72px] sm:text-[104px] md:text-[128px] lg:text-[168px] karibu-rise"
                        >
                            <span>Karibu</span>
                            <span className="text-[#E54D2E]">.</span>
                        </h1>
                        <p
                            className="mt-6 md:mt-8 max-w-2xl font-display text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-zinc-900 fade-rise"
                            style={{ animationDelay: "0.15s" }}
                        >
                            A co-working community of builders shipping together
                            from{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10">
                                    rural Kenya
                                </span>
                                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#E54D2E]/30 -z-0" />
                            </span>
                            .
                        </p>
                        <p
                            className="mt-6 max-w-xl text-base sm:text-lg text-zinc-600 leading-relaxed fade-rise"
                            style={{ animationDelay: "0.25s" }}
                        >
                            We’re looking for builders who want to build a
                            community they’ll actually invest in. Community.
                            Accountability. Ship together.
                        </p>

                        <div
                            className="mt-8 md:mt-10 flex flex-wrap items-center gap-3 fade-rise"
                            style={{ animationDelay: "0.35s" }}
                        >
                            <button
                                type="button"
                                onClick={onRegister}
                                data-testid="hero-register-btn"
                                className="group inline-flex items-center gap-2 h-14 px-7 rounded-full bg-zinc-950 text-white text-base font-medium hover:bg-[#E54D2E] transition-all duration-200"
                            >
                                Register for Event
                                <span className="h-7 w-7 rounded-full bg-white/15 inline-flex items-center justify-center group-hover:bg-white/25">
                                    <Sparkles className="h-3.5 w-3.5" />
                                </span>
                            </button>
                            <a
                                href="#pillars"
                                data-testid="hero-explore-link"
                                className="inline-flex items-center gap-2 h-14 px-6 rounded-full bg-transparent text-zinc-900 text-base font-medium hover:bg-black/5 transition-colors"
                            >
                                See what we do
                                <ArrowDown className="h-4 w-4" />
                            </a>
                        </div>
                    </div>

                    {/* Right: sliding image carousel + stats */}
                    <div
                        className="lg:col-span-4 flex flex-col gap-4 fade-rise"
                        style={{ animationDelay: "0.4s" }}
                    >
                        <HeroImageCarousel />
                        <div className="grid grid-cols-2 gap-4">
                            <div className="relative overflow-hidden rounded-2xl bg-[#E54D2E] aspect-square flex flex-col justify-between p-5 text-white">
                                <span className="text-xs font-medium uppercase tracking-[0.18em] opacity-90">
                                    Cohort
                                </span>
                                <div className="font-display text-5xl font-black leading-none">
                                    06
                                </div>
                                <span className="text-sm leading-tight opacity-90">
                                    IRL hackathons shipped across 4 cities.
                                </span>
                            </div>
                            <div className="relative overflow-hidden rounded-2xl bg-white border border-black/5 aspect-square">
                                <img
                                    src={MOCKUP}
                                    alt="Build session preview"
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroImageCarousel() {
    const [api, setApi] = useState();
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);

    const onSelect = useCallback((carouselApi) => {
        setCurrent(carouselApi.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!api) return undefined;

        onSelect(api);
        api.on("reInit", onSelect);
        api.on("select", onSelect);

        return () => {
            api.off("select", onSelect);
            api.off("reInit", onSelect);
        };
    }, [api, onSelect]);

    useEffect(() => {
        if (!api || paused) return undefined;

        const id = window.setInterval(() => {
            api.scrollNext();
        }, 4800);

        return () => window.clearInterval(id);
    }, [api, paused]);

    return (
        <div
            className="relative rounded-2xl bg-zinc-100 shadow-xl shadow-zinc-950/10 ring-1 ring-black/5 overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <Carousel
                setApi={setApi}
                opts={{
                    loop: true,
                    align: "start",
                }}
                className="w-full"
                aria-label="Community photos"
            >
                <CarouselContent className="-ml-0">
                    {HERO_SLIDES.map((slide, index) => (
                        <CarouselItem
                            key={slide.src}
                            className="pl-0 basis-full"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-zinc-200">
                                <img
                                    src={slide.src}
                                    alt={slide.alt}
                                    className="h-full w-full object-cover"
                                    loading={index === 0 ? "eager" : "lazy"}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-white/90 backdrop-blur text-[11px] font-medium text-zinc-900 shadow-sm">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#E54D2E]" />
                                    {slide.badge}
                                </div>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
            </Carousel>

            <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-2">
                <button
                    type="button"
                    aria-label="Previous slide"
                    className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-zinc-900 shadow-md backdrop-blur transition hover:bg-white"
                    onClick={() => api?.scrollPrev()}
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                    type="button"
                    aria-label="Next slide"
                    className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-zinc-900 shadow-md backdrop-blur transition hover:bg-white"
                    onClick={() => api?.scrollNext()}
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>

            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                {HERO_SLIDES.map((slide, index) => (
                    <button
                        key={slide.src}
                        type="button"
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={current === index}
                        className={cn(
                            "h-2 rounded-full transition-all duration-300",
                            current === index
                                ? "w-8 bg-white shadow-sm"
                                : "w-2 bg-white/45 hover:bg-white/70",
                        )}
                        onClick={() => api?.scrollTo(index)}
                    />
                ))}
            </div>
        </div>
    );
}
